import type { AvailableTransitionsShort } from "@/types/ProductService.types";
import { normalizeStatusForComparison } from "@/utils/activeProjectStatusUtils";
import { getProductSpecificationFlags } from "@/utils/productSpecificationTypeUtils";

/** Food Safety nested tab index: 2 = Other Attachments */
export const FOOD_SAFETY_OTHER_ATTACHMENTS_TAB_INDEX = 2;

const ALL_BRANDED_PRODUCTS = "All Branded Products";
const TO_BE_DISCONTINUED_VENDOR_ASSESSMENT_STATUS = "to_be_discontinued";


/** True when vendor assessment status is in the to-be-discontinued pipeline. */
export function isVendorAssessmentToBeDiscontinued(
  vendorAssessmentStatus?: string | null
): boolean {
  return (
    normalizeStatusForComparison(String(vendorAssessmentStatus ?? "")) ===
    TO_BE_DISCONTINUED_VENDOR_ASSESSMENT_STATUS
  );
}

const normalizeSpecKey = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");

export function isAllBrandedProductsSpecification(spec: unknown): boolean {
  const key = normalizeSpecKey(spec);
  return (
    key === normalizeSpecKey(ALL_BRANDED_PRODUCTS) || key.includes("allbranded")
  );
}

export function isProduceRacBrandedProductsSpecification(spec: unknown): boolean {
  return getProductSpecificationFlags(String(spec ?? "")).isProduceRacBranded;
}

/** Last active Food Safety site id (used for discontinuation section on Other Attachments). */
export function getLastActiveFoodSafetySiteId(
  sites: Array<{ id?: string; siteId?: string; isActive?: boolean }> | null | undefined
): string | null {
  if (!Array.isArray(sites) || sites.length === 0) {
    return null;
  }
  const activeSites = sites.filter((site) => site?.isActive !== false);
  if (activeSites.length === 0) {
    return null;
  }
  const lastActiveSite = activeSites[activeSites.length - 1];
  if (!lastActiveSite) {
    return null;
  }
  const lastId = lastActiveSite.id ?? lastActiveSite.siteId;
  const key = lastId != null ? String(lastId).trim() : "";
  return key || null;
}

/** Must match BE `availableTransitions.data[].label` when wired. */
export const DISCONTINUATION_WORKFLOW_TRANSITION_LABELS = {
  requestToDiscontinued: "request_to_discontinued",
  /** Complete discontinuation — API transition label `proceed_to_discontinued` */
  completeDiscontinuation: "proceed_to_discontinued",
  cancelProject: "cancel_project",
  /** Launched Products — return discontinuation request to prior state */
  returnToDiscontinued: "return_to_discontinued",
  /** Cancel discontinuation request — current API label */
  cancelToDiscontinuedProcess: "cancel_to_discontinued_process",
} as const;

export type DiscontinuationWorkflowSurface =
  | "other-labeling"
  | "food-safety"
  | "main-details";

export type DiscontinuationRouteKind =
  | "launched-products"
  | "active-projects"
  | "ready-for-orders"
  | "other";

export function resolveDiscontinuationRouteKind(pathname: string | null | undefined): DiscontinuationRouteKind {
  const path = String(pathname ?? "");
  if (path.includes("/launched-products")) return "launched-products";
  if (path.includes("/ready-for-orders")) return "ready-for-orders";
  if (path.includes("/active-projects")) return "active-projects";
  return "other";
}

/** Product spec gates for discontinuation workflow buttons and sections. */
export function isDiscontinuationWorkflowSpecEligible(
  surface: DiscontinuationWorkflowSurface,
  normalizedSpecType: string
): boolean {
  const isAllBranded = isAllBrandedProductsSpecification(normalizedSpecType);
  const isProduceRacBranded = isProduceRacBrandedProductsSpecification(normalizedSpecType);

  /** Main Details cancel project — All Branded or Produce RAC Branded (Active Projects). */
  if (surface === "main-details") {
    return isAllBranded || isProduceRacBranded;
  }

  /** Food Safety tab — Launched Products discontinuation workflows (unchanged; not used by Main Details). */
  if (surface === "food-safety") {
    return isAllBranded || isProduceRacBranded;
  }

  return !isAllBranded && !isProduceRacBranded;
}

export function hasDiscontinuationWorkflowTransition(
  transitionsObj: AvailableTransitionsShort | null | undefined,
  transitionLabel: string
): boolean {
  const data = transitionsObj?.data;
  if (!Array.isArray(data)) {
    return false;
  }
  const expected = String(transitionLabel ?? "").trim();
  if (!expected) return false;
  return data.some((transition) => String(transition?.label ?? "").trim() === expected);
}

export function hasRequestToDiscontinuedTransition(
  transitionsObj: AvailableTransitionsShort | null | undefined
): boolean {
  return hasDiscontinuationWorkflowTransition(
    transitionsObj,
    DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.requestToDiscontinued
  );
}

export type ResubmitDiscontinuationButtonLabelParams = {
  vendorAssessmentStatus?: string | null;
  workflowStatus?: readonly unknown[] | null;
  availableTransitions?: AvailableTransitionsShort | null | undefined;
};

/** Resubmit label when VA is `to_be_discontinued`, or workflow is `return_to_cm_discontinued` with `request_to_discontinued`. */
export function shouldUseResubmitDiscontinuationButtonLabel(
  params?: string | null | ResubmitDiscontinuationButtonLabelParams
): boolean {
  if (params != null && typeof params === "object") {
    if (isVendorAssessmentToBeDiscontinued(params.vendorAssessmentStatus)) {
      return true;
    }
    return (
      workflowStatusIncludesReturnToCmDiscontinued(params.workflowStatus) &&
      hasDiscontinuationWorkflowTransition(
        params.availableTransitions,
        DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.requestToDiscontinued
      )
    );
  }
  return isVendorAssessmentToBeDiscontinued(params);
}

export function resolveDiscontinueProductButtonText(params: {
  vendorAssessmentStatus?: string | null;
  workflowStatus?: readonly unknown[] | null;
  availableTransitions?: AvailableTransitionsShort | null | undefined;
  discontinueProductLabel?: string;
  resubmitDiscontinuationLabel?: string;
}): string {
  return resolveDiscontinueProductButtonConfig(params).text;
}

export function resolveDiscontinueProductButtonConfig(params: {
  vendorAssessmentStatus?: string | null;
  workflowStatus?: readonly unknown[] | null;
  availableTransitions?: AvailableTransitionsShort | null | undefined;
  discontinueProductLabel?: string;
  resubmitDiscontinuationLabel?: string;
}): { text: string; primary: boolean } {
  const isResubmit = shouldUseResubmitDiscontinuationButtonLabel({
    vendorAssessmentStatus: params.vendorAssessmentStatus,
    workflowStatus: params.workflowStatus,
    availableTransitions: params.availableTransitions,
  });
  return {
    text: isResubmit
      ? params.resubmitDiscontinuationLabel ?? "Resubmit discontinuation"
      : params.discontinueProductLabel ?? "Discontinue product",
    primary: isResubmit,
  };
}

/** Translation keys for {@link translateWorkflowButtons} (Main Details workflow hook). */
export function resolveDiscontinueProductButtonTranslationKey(params: {
  vendorAssessmentStatus?: string | null;
  workflowStatus?: readonly unknown[] | null;
  availableTransitions?: AvailableTransitionsShort | null | undefined;
}): "RESUBMIT_DISCONTINUATION" | "DISCONTINUE_PRODUCT" {
  return shouldUseResubmitDiscontinuationButtonLabel(params)
    ? "RESUBMIT_DISCONTINUATION"
    : "DISCONTINUE_PRODUCT";
}

export function resolveCancelDiscontinuationRequestButtonTranslationKey(params: {
  workflowStatus?: readonly unknown[] | null;
  availableTransitions: AvailableTransitionsShort | null | undefined;
}): "CANCEL_DISCONTINUATION" | "CANCEL_DISCONTINUATION_REQUEST" {
  return shouldUseCancelDiscontinuationButtonLabel(params)
    ? "CANCEL_DISCONTINUATION"
    : "CANCEL_DISCONTINUATION_REQUEST";
}

function matchesDiscontinuationWorkflowActionCriteria(params: {
  surface: DiscontinuationWorkflowSurface;
  routeKind: DiscontinuationRouteKind;
  normalizedSpecType: string;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  transitionLabel: string;
  eligible: boolean;
}): boolean {
  if (!params.eligible) {
    return false;
  }
  if (!isDiscontinuationWorkflowSpecEligible(params.surface, params.normalizedSpecType)) {
    return false;
  }

  // Discontinuation workflow actions are only available on Launched Products for all surfaces.
  if (params.routeKind !== "launched-products") {
    return false;
  }

  return hasDiscontinuationWorkflowTransition(
    params.availableTransitions,
    params.transitionLabel
  );
}

export function shouldShowDiscontinuationWorkflowAction(params: {
  surface: DiscontinuationWorkflowSurface;
  routeKind: DiscontinuationRouteKind;
  normalizedSpecType: string;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  transitionLabel: string;
  eligible: boolean;
}): boolean {
  
  return matchesDiscontinuationWorkflowActionCriteria(params);
}

/** Main Details — shared gate for all discontinuation workflow buttons (transition label varies per button). */
export function shouldShowMainDetailsDiscontinuationAction(params: {
  isLaunchedProductsPath: boolean;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  normalizedSpecType?: string;
  transitionLabel: string;
  eligible: boolean;
}): boolean {
  return matchesDiscontinuationWorkflowActionCriteria({
    surface: "main-details",
    routeKind: params.isLaunchedProductsPath ? "launched-products" : "other",
    normalizedSpecType: params.normalizedSpecType ?? "",
    availableTransitions: params.availableTransitions,
    transitionLabel: params.transitionLabel,
    eligible: params.eligible,
  });
}

/** Main Details — Cancel discontinuation request when cancel or return transition is available. */
export function shouldShowMainDetailsCancelDiscontinuationRequestAction(params: {
  isLaunchedProductsPath: boolean;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  normalizedSpecType?: string;
  eligible: boolean;
}): boolean {
  return (
    shouldShowMainDetailsDiscontinuationAction({
      ...params,
      transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess,
    }) ||
    shouldShowMainDetailsDiscontinuationAction({
      ...params,
      transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued,
    })
  );
}

/** Main Details — Complete discontinuation when `proceed_to_discontinued` transition is available. */
export function shouldShowMainDetailsCompleteDiscontinuationAction(params: {
  isLaunchedProductsPath: boolean;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  normalizedSpecType?: string;
  eligible: boolean;
}): boolean {
  return shouldShowMainDetailsDiscontinuationAction({
    ...params,
    transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.completeDiscontinuation,
  });
}

/** @deprecated Prefer {@link shouldShowDiscontinuationWorkflowAction} with spec + transition. */
export function shouldShowDiscontinueProductButtonOnLaunchedProducts(params: {
  isLaunchedProductsPath: boolean;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  normalizedSpecType?: string;
  eligible: boolean;
}): boolean {
  return shouldShowDiscontinuationWorkflowAction({
    surface: "other-labeling",
    routeKind: params.isLaunchedProductsPath ? "launched-products" : "other",
    normalizedSpecType: params.normalizedSpecType ?? "",
    availableTransitions: params.availableTransitions,
    transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.requestToDiscontinued,
    eligible: params.eligible,
  });
}

/** Show Cancel discontinuation request when listing exposes cancel or return transition. */
export function shouldShowCancelDiscontinuationRequestButton(params: {
  surface: DiscontinuationWorkflowSurface;
  routeKind: DiscontinuationRouteKind;
  normalizedSpecType: string;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  eligible: boolean;
}): boolean {
  return (
    shouldShowDiscontinuationWorkflowAction({
      ...params,
      transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess,
    }) ||
    shouldShowDiscontinuationWorkflowAction({
      ...params,
      transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued,
    })
  );
}

export type CancelDiscontinuationRequestTransitionLabel =
  | typeof DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess
  | typeof DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued;

/** Active cancel-request transition label from listing (`cancel_to_discontinued_process` preferred). */
export function resolveCancelDiscontinuationRequestTransitionLabel(
  availableTransitions: AvailableTransitionsShort | null | undefined
): CancelDiscontinuationRequestTransitionLabel | null {
  if (
    hasDiscontinuationWorkflowTransition(
      availableTransitions,
      DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess
    )
  ) {
    return DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess;
  }
  if (
    hasDiscontinuationWorkflowTransition(
      availableTransitions,
      DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued
    )
  ) {
    return DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued;
  }
  return null;
}

const RETURN_TO_CM_DISCONTINUED_WORKFLOW_STATUS = "return_to_cm_discontinued";

export function workflowStatusIncludesReturnToCmDiscontinued(
  workflowStatus?: readonly unknown[] | null
): boolean {
  if (!Array.isArray(workflowStatus)) {
    return false;
  }
  return workflowStatus.some(
    (item) =>
      normalizeStatusForComparison(String(item ?? "")) ===
      RETURN_TO_CM_DISCONTINUED_WORKFLOW_STATUS
  );
}

/** Shorter cancel label when returned to CM and cancel transition is available. */
export function shouldUseCancelDiscontinuationButtonLabel(params: {
  workflowStatus?: readonly unknown[] | null;
  availableTransitions: AvailableTransitionsShort | null | undefined;
}): boolean {
  return (
    workflowStatusIncludesReturnToCmDiscontinued(params.workflowStatus) &&
    hasDiscontinuationWorkflowTransition(
      params.availableTransitions,
      DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess
    )
  );
}

export function resolveCancelDiscontinuationRequestButtonText(params: {
  workflowStatus?: readonly unknown[] | null;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  cancelDiscontinuationRequestLabel?: string;
  cancelDiscontinuationLabel?: string;
}): string {
  if (shouldUseCancelDiscontinuationButtonLabel(params)) {
    return params.cancelDiscontinuationLabel ?? "Cancel discontinuation";
  }
  return params.cancelDiscontinuationRequestLabel ?? "Cancel discontinuation request";
}

export function shouldShowCompleteDiscontinuationButton(params: {
  surface: DiscontinuationWorkflowSurface;
  routeKind: DiscontinuationRouteKind;
  normalizedSpecType: string;
  availableTransitions: AvailableTransitionsShort | null | undefined;
  eligible: boolean;
}): boolean {
  return shouldShowDiscontinuationWorkflowAction({
    ...params,
    transitionLabel: DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.completeDiscontinuation,
  });
}

type ProductInfoLike = Record<string, unknown> | null | undefined;

/** Assigned Category Manager Entra id from product info (`categoryManagerUser.portalUserId`). */
export function resolveAssignedCategoryManagerPortalUserId(
  ...sources: ProductInfoLike[]
): string {
  for (const source of sources) {
    if (!source || typeof source !== "object") {
      continue;
    }
    const cm = source.categoryManagerUser;
    if (!cm || typeof cm !== "object") {
      continue;
    }
    const portalUserId = String((cm as Record<string, unknown>).portalUserId ?? "").trim();
    if (portalUserId) {
      return portalUserId;
    }
  }
  return "";
}

function parseNotProgressedFlag(raw: unknown): boolean {
  if (raw === true) {
    return true;
  }
  if (raw === false || raw == null) {
    return false;
  }
  return String(raw).trim().toLowerCase() === "true";
}

/** Reads `vendorList[*].notProgressed` from `/productInfo/details` for the current assessment. */
export function isVendorAssessmentNotProgressed(params: {
  vendorAssessmentId?: string | number | null;
  productInfoSources: ProductInfoLike[];
}): boolean {
  const targetId = String(params.vendorAssessmentId ?? "").trim();

  for (const source of params.productInfoSources) {
    if (!source || typeof source !== "object") {
      continue;
    }
    const vendorDetails = source.vendorDetails;
    if (!Array.isArray(vendorDetails)) {
      continue;
    }

    for (const vendorDetail of vendorDetails) {
      if (vendorDetail == null || typeof vendorDetail !== "object") {
        continue;
      }
      const vendorList = (vendorDetail as Record<string, unknown>).vendorList;
      if (!Array.isArray(vendorList)) {
        continue;
      }

      for (const row of vendorList) {
        if (row == null || typeof row !== "object") {
          continue;
        }
        const record = row as Record<string, unknown>;
        if (targetId) {
          const assessmentId = String(record.assessmentId ?? "").trim();
          if (assessmentId !== targetId) {
            continue;
          }
        }

        return parseNotProgressedFlag(record.notProgressed ?? record.not_progressed);
      }
    }
  }

  return false;
}

/** True when logged-in Entra id matches assigned CM on product info or listing GET. */
export function isLoggedInUserAssignedCategoryManager(params: {
  loggedInPortalUserId: string;
  productInfoSources: ProductInfoLike[];
  /** Fallback from `GET .../discontinue-not-progressed` → `categoryManagerUserDetails.portalUserId`. */
  assignedCategoryManagerPortalUserId?: string;
}): boolean {
  const loggedIn = String(params.loggedInPortalUserId ?? "").trim();
  if (!loggedIn) {
    return false;
  }
  const assigned =
    resolveAssignedCategoryManagerPortalUserId(...params.productInfoSources) ||
    String(params.assignedCategoryManagerPortalUserId ?? "").trim();
  if (!assigned) {
    return false;
  }
  return loggedIn.toLowerCase() === assigned.toLowerCase();
}

export function shouldShowCancelProjectDiscontinuationButton(params: {
  surface: DiscontinuationWorkflowSurface;
  routeKind: DiscontinuationRouteKind;
  normalizedSpecType: string;
  vendorAssessmentStatus: string;
  vendorAssessmentId?: string | number | null;
  loggedInPortalUserId: string;
  productInfoSources: ProductInfoLike[];
  /** From `GET .../discontinue-not-progressed` when product info is not yet updated. */
  listingNotProgressed?: boolean;
  assignedCategoryManagerPortalUserId?: string;
  /** From `useIsBusinessAdmin` — Business Admin can see Cancel Project (same as assigned CM). */
  isBusinessAdmin?: boolean;
}): boolean {

  if (params.routeKind !== "active-projects") {
    return false;
  }

  if (!isDiscontinuationWorkflowSpecEligible(params.surface, params.normalizedSpecType)) {
    return false;
  }

  if (
    params.listingNotProgressed === true ||
    isVendorAssessmentNotProgressed({
      vendorAssessmentId: params.vendorAssessmentId,
      productInfoSources: params.productInfoSources,
    })
  ) {
    return false;
  }

  if (params?.isBusinessAdmin) {
    return true;
  }

  return isLoggedInUserAssignedCategoryManager({
    loggedInPortalUserId: params.loggedInPortalUserId,
    productInfoSources: params.productInfoSources,
    assignedCategoryManagerPortalUserId: params.assignedCategoryManagerPortalUserId,
  });
}

export function findTransitionRoleFromTransitionLabel(
  transitionLabel: string | null | undefined
): "CATEGORY_MANAGER" | "BUYER" | "" {
  const transitionLabelTrimmed = String(transitionLabel ?? "").trim();
  if (
    transitionLabelTrimmed ===
    DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.cancelToDiscontinuedProcess
  ) {
    return "CATEGORY_MANAGER";
  }
  if (
    transitionLabelTrimmed ===
    DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued
  ) {
    return "BUYER";
  }
  return "";
}